import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI, authAPI } from '../services/api';
import Logo from '../components/Logo';
import WelcomeBanner from '../components/WelcomeBanner';
import FloatingShapes from '../components/FloatingShapes';
import Footer from '../components/Footer';
import FeedbackButton from '../components/FeedbackButton';
import { BarChart3, Zap, CreditCard, LogOut, FileText, Sparkles, User } from 'lucide-react';

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      setUser(userData);

      const [subResponse, usageResponse] = await Promise.all([
        subscriptionAPI.getCurrentSubscription(),
        subscriptionAPI.getUsage()
      ]);

      setSubscription(subResponse.data.subscription);
      setUsage(usageResponse.data.usage);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
overflow: 'auto',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ fontSize: '18px', color: '#2e7d32' }}>Loading...</div>
      </div>
    );
  }

  const planColors = {
    free: '#9e9e9e',
    teacher: '#66bb6a',
    school: '#42a5f5'
  };

  const planNames = {
    free: 'Free Plan',
    teacher: 'Teacher Plan',
    school: 'School Plan'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%)',
position: 'relative'
    }}>
<FloatingShapes />
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '20px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Logo size="medium" showTagline={false} />
<p style={{ fontSize: '14px', color: '#666', margin: '0 0 0 60px' }}>
  Welcome back, {user?.firstName}!
</p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'white',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            color: '#424242',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.target.style.borderColor = '#ef5350';
            e.target.style.color = '#ef5350';
          }}
          onMouseOut={(e) => {
            e.target.style.borderColor = '#e0e0e0';
            e.target.style.color = '#424242';
          }}
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>

      {/* Tagline Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        padding: '16px',
        textAlign: 'center',
        borderBottom: '3px solid #81c784'
      }}>
        <p style={{
          color: 'white',
          fontSize: '16px',
          fontWeight: '600',
          margin: 0,
          letterSpacing: '0.5px'
        }}>
          ✨ Built for Teachers, by a Teacher ✨
        </p>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px' }}>
<WelcomeBanner userName={user?.firstName} />

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Current Plan Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: `${planColors[subscription?.plan_type]}20`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CreditCard size={20} color={planColors[subscription?.plan_type]} />
              </div>
              <div>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Current Plan</p>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: planColors[subscription?.plan_type],
                  margin: 0
                }}>
                  {planNames[subscription?.plan_type]}
                </h3>
              </div>
            </div>
            {subscription?.plan_type === 'free' && (
              <button
                onClick={() => navigate('/plans')}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Upgrade Now
              </button>
            )}
          </div>

          {/* Usage Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#fff9c420',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Zap size={20} color="#fdd835" />
              </div>
              <div>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>This Month</p>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#424242', margin: 0 }}>
                  {usage?.remaining !== null ? usage?.remaining : '∞'} remaining
                </h3>
              </div>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              background: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: usage?.is_unlimited ? '100%' : `${((usage?.limit - usage?.remaining) / usage?.limit) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #66bb6a 0%, #42a5f5 100%)',
                transition: 'width 0.3s'
              }} />
            </div>
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              {usage?.is_unlimited ? 'Unlimited generations' : `${usage?.limit - usage?.remaining} of ${usage?.limit} used`}
            </p>
          </div>

          {/* Generate Card */}
          <div style={{
            background: 'linear-gradient(135deg, #66bb6a 0%, #42a5f5 100%)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 12px rgba(102, 187, 106, 0.3)',
            color: 'white',
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
            onClick={() => navigate('/generate')}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <FileText size={32} />
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
                  Generate Materials
                </h3>
                <p style={{ fontSize: '14px', margin: 0, opacity: 0.9 }}>
                  Create worksheets, projects & more
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#2e7d32', marginBottom: '24px' }}>
            Quick Actions
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
            <button
              onClick={() => navigate('/generate')}
              style={{
                padding: '16px',
                background: '#f5f5f5',
                border: '2px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#66bb6a';
                e.target.style.background = '#e8f5e9';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.background = '#f5f5f5';
              }}
            >
              <FileText size={24} color="#66bb6a" style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#424242', margin: 0 }}>
                New Material
              </p>
            </button>

            <button
              onClick={() => navigate('/plans')}
              style={{
                padding: '16px',
                background: '#f5f5f5',
                border: '2px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#42a5f5';
                e.target.style.background = '#e3f2fd';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.background = '#f5f5f5';
              }}
            >
              <BarChart3 size={24} color="#42a5f5" style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#424242', margin: 0 }}>
                View Plans
              </p>
            </button>

            <button
              onClick={() => navigate('/history')}
              style={{
                padding: '16px',
                background: '#f5f5f5',
                border: '2px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#fbc02d';
                e.target.style.background = '#fff9c4';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.background = '#f5f5f5';
              }}
            >
              <FileText size={24} color="#fbc02d" style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#424242', margin: 0 }}>
                Material History
              </p>
            </button>

            <button
              onClick={() => navigate('/profile')}
              style={{
                padding: '16px',
                background: '#f5f5f5',
                border: '2px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#9c27b0';
                e.target.style.background = '#f3e5f5';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = 'transparent';
                e.target.style.background = '#f5f5f5';
              }}
            >
              <User size={24} color="#9c27b0" style={{ marginBottom: '8px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#424242', margin: 0 }}>
                My Profile
              </p>
            </button>
         </div>
        </div>
      </div>
<FeedbackButton />
      <Footer />
    </div>
  );
}